import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { EDITOR_COLOR_SCHEMES, type EditorColorSelection } from '../../../../core/theme/monacoColorSchemes';
import { getSwatchesForSelection, type CustomEditorColors } from '../../../../core/theme/customEditorColors';

/** 4 small round swatches (keyword, string, number, comment) for one option's colors. */
function SchemeSwatches({ colors }: { colors: string[] }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5">
      {colors.map((color, i) => (
        <span key={i} className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

const OPTION_IDS: EditorColorSelection[] = [...EDITOR_COLOR_SCHEMES.map((s) => s.id), 'custom'];

function labelFor(id: EditorColorSelection): string {
  if (id === 'custom') return 'Custom';
  return EDITOR_COLOR_SCHEMES.find((scheme) => scheme.id === id)?.label ?? id;
}

interface EditorColorSchemeSelectProps {
  selection: EditorColorSelection;
  isDarkMode: boolean;
  customEditorColors: CustomEditorColors | null;
  onSelect: (id: EditorColorSelection) => void;
  /** Fires on hover-move over an open option with its id, and on hover-leave / dropdown-close with `undefined`. Must never touch the store. */
  onHoverPreview: (id: EditorColorSelection | undefined) => void;
  /**
   * Controlled open state, lifted to the dialog. Two independently-bundled
   * copies of `@radix-ui/react-dismissable-layer` ship in this dependency
   * tree — Select's own copy (`@radix-ui/react-select/node_modules/...`)
   * and Dialog's (`@radix-ui/react-dialog/node_modules/...`) — so the two
   * primitives do NOT share one layer stack: each treats itself as "the
   * topmost layer" on every Escape press, so an UNCONTROLLED Select nested
   * in a Dialog would close BOTH simultaneously instead of just itself. The
   * dialog intercepts Escape via `DialogContent`'s own `onEscapeKeyDown`
   * (calling `event.preventDefault()` to keep itself open, since Select's
   * own dismiss-on-escape gets suppressed the same way by that shared
   * `event.defaultPrevented` flag) and closes this dropdown by flipping
   * `open` itself — see `EditorColorSchemeDialog`.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The scheme dropdown (5 presets + Custom) in the Editor colors dialog — a
 * compact shadcn `Select` replacing the old always-expanded 6-row list
 * (`EditorColorSchemeList`, removed). Selecting an option calls `onSelect`;
 * hovering an open option previews it via `onHoverPreview` without touching
 * the draft.
 *
 * `onMouseMove` (not `onMouseEnter`/Radix's own composed `onPointerMove`)
 * drives the preview — same reasoning `EditorColorSchemeList` documented:
 * a stationary cursor already sitting over the popover's first item when it
 * renders can spuriously re-fire an *enter*-style event with no matching
 * *leave* ever following, which would stick the preview on that option.
 * `mousemove` only fires on genuine pointer displacement. The dropdown
 * CLOSING (Escape, an outside click, or picking an option) always clears the
 * preview via `Select`'s own `onOpenChange` — once the popover is gone
 * there's no more hovering left to revert it.
 *
 * `side="bottom"` + `avoidCollisions={false}` pin the popover below the
 * trigger unconditionally. The trigger sits directly under the dialog's two
 * preview panes — if Radix's own collision-avoidance flip ever kicked in,
 * the open popover would cover the very panes the hover-preview updates.
 */
export function EditorColorSchemeSelect({
  selection,
  isDarkMode,
  customEditorColors,
  onSelect,
  onHoverPreview,
  open,
  onOpenChange,
}: EditorColorSchemeSelectProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground">Scheme</span>
      <Select
        value={selection}
        onValueChange={(value) => onSelect(value as EditorColorSelection)}
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) onHoverPreview(undefined);
        }}
      >
        <SelectTrigger className="w-[240px]" data-testid="editor-color-scheme-select">
          {/*
            A `div` (not a `span`) wraps the trigger's content: `SelectTrigger`
            clamps every DIRECT span child to one line (`[&>span]:line-clamp-1`,
            i.e. `display: -webkit-box`), which would beat `flex` on this row and
            drop `justify-between` — the swatches would sit next to the label
            again instead of at the right edge.
          */}
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <SelectValue className="truncate">{labelFor(selection)}</SelectValue>
            <SchemeSwatches colors={getSwatchesForSelection(selection, isDarkMode, customEditorColors)} />
          </div>
        </SelectTrigger>
        <SelectContent side="bottom" avoidCollisions={false}>
          {OPTION_IDS.map((id) => (
            <SelectItem
              key={id}
              value={id}
              // `SelectItem` renders its children inside Radix's `ItemText`
              // span (the item's last child; the check indicator is the
              // absolutely-positioned first one). That span is content-sized
              // by default, so it has to grow for the row below to be able to
              // push the swatches to the right edge.
              className="[&>span:last-child]:flex-1"
              data-testid={`editor-color-scheme-option-${id}`}
              onMouseMove={() => onHoverPreview(id)}
              onMouseLeave={() => onHoverPreview(undefined)}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate">{labelFor(id)}</span>
                <SchemeSwatches colors={getSwatchesForSelection(id, isDarkMode, customEditorColors)} />
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
